import type { PayrollImportLine, PayrollPeriodImport } from '../types';

const EXCLUDED_OUTSOURCED_STAFF = new Set([
  'sooraj soman',
  'mukesh kumar',
  'safvana',
]);

function normName(v: unknown): string {
  return String(v ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function isExcludedOutsourcedStaffName(name: unknown): boolean {
  return EXCLUDED_OUTSOURCED_STAFF.has(normName(name));
}

function asFiniteNumber(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Gross pay for payroll lines that are employee wages (excluding outsourced staff that are invoiced as expenses).
 */
export function grossPayExcludingOutsourcedStaff(lines: PayrollImportLine[] | undefined): number {
  if (!lines?.length) return 0;
  return lines.reduce((sum, line) => {
    if (isExcludedOutsourcedStaffName(line?.employeeName)) return sum;
    return sum + asFiniteNumber(line?.gross);
  }, 0);
}

/**
 * Gross pay used in reports after excluding outsourced invoiced staff.
 * Falls back to totals when no line-level gross values are available.
 */
export function reportableGrossPayFromImport(
  imp: Pick<PayrollPeriodImport, 'lines' | 'totals'> | { lines?: PayrollImportLine[]; totals?: { totalGrossPay?: number } }
): number {
  const hasAnyLineGross =
    Array.isArray(imp.lines) && imp.lines.some((l) => Number.isFinite(Number(l?.gross)));
  const lineGross = grossPayExcludingOutsourcedStaff(imp.lines);
  if (hasAnyLineGross) return Math.max(0, lineGross);
  return Math.max(0, asFiniteNumber(imp.totals?.totalGrossPay));
}

